import { useEffect, useState } from 'react';

interface Response<T> {
  ok: boolean;
  data?: T;
}

// Requests in flight per cache and key: components mounting together (e.g. every card of a list)
// share one request instead of each sending its own.
const inFlight = new WeakMap<Map<string, unknown>, Map<string, Promise<Response<unknown>>>>();

const request = <T>(cache: Map<string, T>, key: string, load: () => Promise<Response<T>>): Promise<Response<T>> => {
  let pending = inFlight.get(cache);
  if (!pending) {
    pending = new Map();
    inFlight.set(cache, pending);
  }
  let promise = pending.get(key) as Promise<Response<T>> | undefined;
  if (!promise) {
    promise = load();
    pending.set(key, promise);
    promise.then(() => pending?.delete(key), () => pending?.delete(key));
  }
  return promise;
};

/**
 * `load()`'s data, cached in `cache` under `key` for the session (so going back to a Home tab or a
 * list doesn't ask again); `null` while it loads and `fallback` if the request fails.
 */
export const useCachedResource = <T>(
  cache: Map<string, T>, key: string, load: () => Promise<Response<T>>, fallback: T
): T | null => {
  const [value, setValue] = useState<T | null>(cache.get(key) || null);

  useEffect(() => {
    const cached = cache.get(key);
    if (cached) {
      setValue(cached);
      return undefined;
    }
    let current = true;
    setValue(null);
    request(cache, key, load).then(res => {
      if (res.ok && res.data) {
        cache.set(key, res.data);
      }
      if (current) {
        setValue(res.ok && res.data ? res.data : fallback);
      }
    });
    return () => {
      current = false;
    };
    // `load` and `fallback` follow from `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, key]);

  return value;
};
